import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle, Warning, MagnifyingGlass } from "@phosphor-icons/react";
import EvalCard from "@/components/EvalCard";

const WINDOWS = [
  { id: "30", label: "Last 30 days" },
  { id: "90", label: "Last 90 days" },
  { id: "year", label: "This year" },
  { id: "all", label: "All time" },
];

function sinceFor(win) {
  if (win === "all") return null;
  if (win === "year") return `${new Date().getFullYear()}-01-01`;
  return new Date(Date.now() - parseInt(win, 10) * 86400000).toISOString().slice(0, 10);
}

export default function HeadCoachEvaluations() {
  const [win, setWin] = useState("90");
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState({ player: "", coach: "", q: "" });
  const [coverageQ, setCoverageQ] = useState("");

  const refresh = () => {
    const since = sinceFor(win);
    api.get("/term-evaluations/club-summary", {
      params: since ? { since } : { all_time: true },
    }).then((r) => setSummary(r.data));
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [win]);

  if (!summary) return <div className="font-label">Loading…</div>;

  const winLabel = WINDOWS.find((w) => w.id === win)?.label || "";
  const coverage = summary.coverage.sort((a, b) => a.evaluated - b.evaluated);
  const evaluated = coverage.filter((c) => c.evaluated > 0);
  const missing = coverage.filter((c) => c.evaluated === 0);
  const coachList = Array.from(new Set(summary.evaluations.map((e) => e.coach_name)));

  const filtered = summary.evaluations.filter((e) => {
    if (filter.player && e.player_id !== filter.player) return false;
    if (filter.coach && e.coach_name !== filter.coach) return false;
    if (filter.q) {
      const q = filter.q.toLowerCase();
      return (
        e.player_name.toLowerCase().includes(q) ||
        e.category_name.toLowerCase().includes(q) ||
        (e.comment || "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  return (
    <div className="space-y-5" data-testid="hc-evaluations">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-label">Admin</div>
          <h1 className="font-heading uppercase text-3xl sm:text-4xl tracking-tight">Evaluations</h1>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <select
              className="px-2.5 py-1 bg-[#dfff00] text-black rounded-md text-xs font-bold uppercase tracking-wider cursor-pointer border-0"
              value={win}
              onChange={(e) => setWin(e.target.value)}
              data-testid="eval-window-select"
            >
              {WINDOWS.map((w) => (
                <option key={w.id} value={w.id}>{w.label}</option>
              ))}
            </select>
            <span className="text-xs text-zinc-500" data-testid="eval-window-summary">
              {summary.total_evaluations} evaluations across {evaluated.length} player{evaluated.length === 1 ? "" : "s"}
            </span>
          </div>
        </div>
      </div>

      {/* Coverage */}
      <section data-testid="coverage-section">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <div className="font-label">Player coverage · {winLabel.toLowerCase()}</div>
          <div className="flex items-center gap-1.5">
            <MagnifyingGlass size={14} className="text-zinc-500" />
            <input
              className="input"
              style={{ height: "2.25rem", width: "14rem" }}
              placeholder="Search player name…"
              value={coverageQ}
              onChange={(e) => setCoverageQ(e.target.value)}
              data-testid="coverage-search"
            />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {coverage
            .filter((c) => !coverageQ || c.player_name.toLowerCase().includes(coverageQ.toLowerCase()))
            .map((c) => (
            <Link
              key={c.player_id}
              to={`/players/${c.player_id}`}
              className="surface p-3 flex items-center justify-between hover:border-[#dfff00]/40 transition-colors"
              data-testid={`coverage-${c.player_id}`}
            >
              <span className="font-semibold text-sm">{c.player_name}</span>
              <span className={`text-xs px-2 py-0.5 rounded font-bold uppercase tracking-wider ${
                c.evaluated === 0
                  ? "bg-[#ff0026]/10 text-[#ff0026]"
                  : c.evaluated < 5
                  ? "bg-[#ffde00]/10 text-[#ffde00]"
                  : "bg-[#16ec06]/10 text-[#16ec06]"
              }`}>
                {c.evaluated === 0 ? <Warning size={11} className="inline" weight="bold" /> : <CheckCircle size={11} className="inline" weight="bold" />}
                {" "}{c.evaluated} rated
              </span>
            </Link>
          ))}
        </div>
        {missing.length > 0 && (
          <div className="text-xs text-zinc-500 mt-2">
            <Warning size={12} weight="bold" className="inline mr-1 text-[#ff0026]" />
            {missing.length} player{missing.length === 1 ? "" : "s"} not evaluated in this window.
          </div>
        )}
      </section>

      {/* Filters */}
      <div className="surface p-3 flex gap-2 flex-wrap items-center" data-testid="eval-filters">
        <div className="flex items-center gap-1.5 flex-1 min-w-[180px]">
          <MagnifyingGlass size={16} className="text-zinc-500" />
          <input
            className="input"
            style={{ height: "2.5rem" }}
            placeholder="Search player, category, comment…"
            value={filter.q}
            onChange={(e) => setFilter({ ...filter, q: e.target.value })}
            data-testid="eval-search"
          />
        </div>
        <select className="input" style={{ height: "2.5rem", width: "auto" }} value={filter.player} onChange={(e) => setFilter({ ...filter, player: e.target.value })} data-testid="filter-player">
          <option value="">All players</option>
          {coverage.map((c) => <option key={c.player_id} value={c.player_id}>{c.player_name}</option>)}
        </select>
        <select className="input" style={{ height: "2.5rem", width: "auto" }} value={filter.coach} onChange={(e) => setFilter({ ...filter, coach: e.target.value })} data-testid="filter-coach">
          <option value="">All coaches</option>
          {coachList.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Evaluations list */}
      <section>
        <div className="font-label mb-2">{filtered.length} matching evaluations</div>
        <div className="grid gap-2">
          {filtered.map((e) => (
            <EvalCard key={e.id} ev={e} showPlayer testid={`eval-${e.id}`} onUpdated={refresh} />
          ))}
          {filtered.length === 0 && <div className="text-sm text-zinc-500">No evaluations match.</div>}
        </div>
      </section>
    </div>
  );
}
